import React, { useState, useMemo, useCallback } from "react";
import {
  CalendarToday as CalendarIcon,
  Cancel as CancelIcon,
  CheckCircleOutline as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  MonetizationOn as MonetizationOnIcon,
  MoreVert as MoreIcon,
  Phone as PhoneIcon,
  QueryBuilder,
  Sell as SellIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  // FilterList as FilterListIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
  Checkbox,
} from "@mui/material";

// Custom styled components for the table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  fontSize: "0.925rem",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  fontFamily: "'Inter', 'Poppins', sans-serif",
  position: "relative",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  lineHeight: 1.6,
  "&.MuiTableCell-head": {
    background: theme.palette.mode === "light"
      ? `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.03)} 0%, 
          ${alpha(theme.palette.primary.light, 0.06)} 30%,
          ${alpha(theme.palette.primary.main, 0.04)} 70%,
          ${alpha(theme.palette.primary.dark, 0.02)} 100%)`
      : `linear-gradient(135deg, 
          ${alpha(theme.palette.grey[900], 0.95)} 0%, 
          ${alpha(theme.palette.grey[800], 0.9)} 50%, 
          ${alpha(theme.palette.grey[900], 0.95)} 100%)`,
    fontWeight: 700,
    color: theme.palette.mode === "light"
      ? alpha(theme.palette.primary.dark, 0.9)
      : theme.palette.primary.light,
    letterSpacing: "0.03em",
    fontSize: "0.9rem",
    textTransform: "none",
    whiteSpace: "normal",
    borderBottom: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    height: 56,
    paddingTop: 10,
    paddingBottom: 10,
    backdropFilter: "blur(12px)",
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -2,
      left: 0,
      right: 0,
      height: "2px",
      background: `linear-gradient(90deg, 
                transparent 0%, 
                ${alpha(theme.palette.primary.main, 0.3)} 30%,
                ${alpha(theme.palette.primary.main, 0.4)} 50%,
                ${alpha(theme.palette.primary.main, 0.3)} 70%,
                transparent 100%)`,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "2px",
      background: theme.palette.mode === "light"
        ? `linear-gradient(90deg, 
            transparent 0%, 
            ${alpha(theme.palette.common.white, 0.9)} 50%, 
            transparent 100%)`
        : alpha(theme.palette.background.paper, 0.2),
      zIndex: 1,
    },
  },
  "&.MuiTableCell-body": {
    color: theme.palette.text.primary,
    fontWeight: 500,
    overflow: "hidden",
    whiteSpace: "normal",
    wordWrap: "break-word",
    lineHeight: 1.5,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  minHeight: 56,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.mode === "light"
      ? alpha(theme.palette.primary.main, 0.025)
      : alpha(theme.palette.primary.main, 0.015),

    "& td": {
      borderBottomColor: alpha(theme.palette.primary.main, 0.1),
    },
  },
  "&:nth-of-type(even)": {
    backgroundColor:
      theme.palette.mode === "light"
        ? alpha(theme.palette.grey[50], 0.5)
        : alpha(theme.palette.grey[900], 0.3),
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:first-of-type": {
    "& td": {
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    },
  },
}));

// Value display components
const ValueChip = styled(Box)(
  ({ theme, color = "default", size = "medium" }) => {
    const getColor = () => {
      switch (color) {
        case "primary":
          return theme.palette.primary;
        case "success":
          return theme.palette.success;
        case "error":
          return theme.palette.error;
        case "warning":
          return theme.palette.warning;
        case "info":
          return theme.palette.info;
        default:
          return theme.palette.grey;
      }
    };

    const colorObj = getColor() || theme.palette.grey;

    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colorObj?.main
        ? alpha(colorObj.main, 0.065)
        : alpha(theme.palette.grey[500], 0.065),
      color: colorObj?.main || theme.palette.grey[700],
      fontWeight: 600,
      fontSize: size === "small" ? "0.81rem" : "0.885rem",
      borderRadius: "18px",
      padding: size === "small" ? "5.5px 14px" : "7px 18px",
      border: `1px solid ${colorObj?.main
          ? alpha(colorObj.main, 0.12)
          : alpha(theme.palette.grey[500], 0.12)
        }`,
      boxShadow: `0 3px 8px ${colorObj?.main
          ? alpha(colorObj.main, 0.08)
          : alpha(theme.palette.grey[500], 0.08)
        }`,
      whiteSpace: "normal",
      maxWidth: "100%",
      overflow: "visible",
      wordWrap: "break-word",
      letterSpacing: "0.025em",
      fontFamily: "'Poppins', sans-serif",
      transition: "all 0.3s ease",
      backdropFilter: "blur(8px)",
      position: "relative",
      "&:hover": {
        boxShadow: `0 4px 12px ${colorObj?.main
            ? alpha(colorObj.main, 0.18)
            : alpha(theme.palette.grey[500], 0.18)
          }`,
        transform: "translateY(-2px)",
        backgroundColor: colorObj?.main
          ? alpha(colorObj.main, 0.085)
          : alpha(theme.palette.grey[500], 0.085),
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "1px",
        background:
          theme.palette.mode === "light"
            ? "rgba(255, 255, 255, 0.85)"
            : alpha(theme.palette.background.paper, 0.15),
        borderTopLeftRadius: "18px",
        borderTopRightRadius: "18px",
      },
    };
  }
);

const TextValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.935rem",
  fontWeight: 500,
  fontFamily: "'Poppins', sans-serif",
  letterSpacing: "0.01em",
  color: theme.palette.text.primary,
  transition: "all 0.2s ease",
  display: "inline-flex",
  alignItems: "center",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));



const CommonTable = ({
  permissions,
  columns,
  data,
  loading,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  activeTab,
  getStatusColor,
  getInitials,
  onViewClick,
  onEditClick,
  onDeleteClick,
  onApplyLoanClick,
  isBorrower,
  customCellRenderer,
  noDataMessage,
  totalRecords,
  handleUpdateStatus,
  onSort,
  currentSortBy,
  currentOrder,
}) => {
  const theme = useTheme();
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuColumnId, setMenuColumnId] = useState(null);
  const [multiFilters, setMultiFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Column width calculation utilities
  const calculateTextWidth = (text, fontFamily) => {
    try {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) return String(text ?? "").length * 8; // Fallback: 8px per character
      context.font = fontFamily;
      return context.measureText(String(text)).width;
    } catch {
      return String(text ?? "").length * 8;
    }
  };

  const getColumnDisplayValue = useCallback((row, columnId) => {
    const column = columns.find((c) => c.id === columnId);
    const rawValue = row?.[columnId];
    const value = column?.transformForFilter ? column.transformForFilter(row) : rawValue;
    if (value === undefined || value === null) return "—";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }, [columns]);

  const computedColumnWidths = useMemo(() => {
    try {
      const headerFont = "600 0.875rem 'Poppins', 'Inter', sans-serif";
      const cellFont = "500 0.935rem 'Poppins', 'Inter', sans-serif";
      const paddingWidth = 16;
      const iconSpace = 16;
      const maxAutoWidth = 260;

      const columnWidths = {};
      columns.forEach((col) => {
        const headerWidth = calculateTextWidth(col.label ?? col.id, headerFont) + paddingWidth;
        
        const sampleRows = (data || []).slice(0, 200);
        let maxCellWidth = 0;
        sampleRows.forEach((row) => {
          const displayText = getColumnDisplayValue(row, col.id);
          const cellWidth = calculateTextWidth(displayText, cellFont) + paddingWidth + iconSpace;
          maxCellWidth = Math.max(maxCellWidth, cellWidth);
        });

        const semanticMinWidth = {
          contact: 220,
          date: 140,
          status: 120,
          eligibilityScore: 200
        }[col.id] || 0;

        const columnMaxWidth = typeof col.maxWidth === "number" ? col.maxWidth : maxAutoWidth;
        const compactWidth = Math.min(Math.max(headerWidth, maxCellWidth), columnMaxWidth);

        const finalWidth = Math.ceil(Math.max(
          compactWidth,
          col.minWidth || 0,
          semanticMinWidth
        ));
        
        columnWidths[col.id] = finalWidth;
      });
      return columnWidths;
    } catch {
      return {};
    }
  }, [columns, data, getColumnDisplayValue]);

  const getColSpan = () => {
    return (
      columns.length + (columns.some((col) => col.id === "actions") ? 0 : 1)
    );
  };

  // const openFilterMenu = (event, columnId) => {
  //   event.stopPropagation();
  //   setMenuAnchor(event.currentTarget);
  //   setMenuColumnId(columnId);
  // };

  const handleSort = (columnId) => {
    let direction = 'asc';
    if (currentSortBy === columnId && currentOrder === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnId, direction });
    
    if (onSort) {
      onSort(columnId, direction);
    }
  };

  const getSortIcon = (columnId) => {
    // Only show icon if this column is currently being sorted
    if (currentSortBy !== columnId) {
      return null;
    }

    const iconProps = {
      fontSize: "small",
      sx: {
        color: theme.palette.primary.main,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
      }
    };

    return currentOrder === 'asc' 
      ? <ArrowUpwardIcon {...iconProps} />
      : <ArrowDownwardIcon {...iconProps} />;
  };

  const closeFilterMenu = () => {
    setMenuAnchor(null);
    setMenuColumnId(null);
  };

  const toggleValue = (columnId, value) => {
    setMultiFilters((prev) => {
      const currentFilters = new Set(prev[columnId] || []);
      if (currentFilters.has(value)) {
        currentFilters.delete(value);
      } else {
        currentFilters.add(value);
      }
      return { ...prev, [columnId]: currentFilters };
    });
  };

  const clearColumnFilter = (columnId) => {
    setMultiFilters((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[columnId];
      return updatedFilters;
    });
  };

  const clearAllFilters = () => {
    setMultiFilters({});
  };

  const hasActiveFilters = useMemo(
    () => Object.values(multiFilters).some((filterSet) => filterSet && filterSet.size > 0),
    [multiFilters]
  );

  const passesMultiFilter = useCallback((columnId, value) => {
    const filterSet = multiFilters[columnId];
    if (!filterSet || filterSet.size === 0) return true;
    
    if (Array.isArray(value)) {
      return value.some((item) => filterSet.has(String(item)));
    }
    return filterSet.has(String(value));
  }, [multiFilters]);

  const filteredTableData = useMemo(() => {
    let filteredData = data;
    
    if (hasActiveFilters) {
      filteredData = data.filter((row) =>
        columns.every((col) => {
          if (col.id === "actions") return true;
          
          const columnValue = col.transformForFilter ? col.transformForFilter(row) : row[col.id];
          return passesMultiFilter(col.id, columnValue);
        })
      );
    }

    // Apply sorting if configured
    if (sortConfig.key) {
      filteredData = [...filteredData].sort((a, b) => {
        const column = columns.find(col => col.id === sortConfig.key);
        if (!column) return 0;

        let aValue = column.transformForFilter ? column.transformForFilter(a) : a[sortConfig.key];
        let bValue = column.transformForFilter ? column.transformForFilter(b) : b[sortConfig.key];

        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [hasActiveFilters, data, columns, sortConfig, passesMultiFilter]);

  const uniqueValuesForColumn = (columnId) => {
    const column = columns.find((c) => c.id === columnId);
    if (column?.staticFilterOptions?.length > 0) {
      return column.staticFilterOptions;
    }    
    const uniqueValues = new Set();
    data.forEach((row) => {
      const value = column?.transformForFilter ? column.transformForFilter(row) : row[columnId];      
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== "") {
            uniqueValues.add(String(item));
          }
        });
      } else if (value !== undefined && value !== null && value !== "") {
        uniqueValues.add(String(value));
      }
    });
    return Array.from(uniqueValues).sort((a, b) => a.localeCompare(b));
  };

  const handleOpenActionMenu = (event, row) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedRow(null);
  };

  // Determine status icon based on status
  const getStatusIcon = (status) => {
    switch (status) {
      case "New":
        return <TimeIcon fontSize="small" sx={{ mr: 1 }} />;
      case "Contacted":
        return <QueryBuilder fontSize="small" sx={{ mr: 1 }} />;
      case "Qualified":
      case "Eligible":
        return <CheckIcon fontSize="small" sx={{ mr: 1 }} />;
      case "Not Eligible":
        return <CancelIcon fontSize="small" sx={{ mr: 1 }} />;
      case "Pending":
        return <QueryBuilder fontSize="small" sx={{ mr: 1 }} />;
      default:
        return null;
    }
  };

  // Create gradient backgrounds for avatars based on status
  const getAvatarGradient = (status, theme) => {
    // Use optional chaining to prevent errors if getStatusColor returns undefined
    const color = getStatusColor ? getStatusColor(status) : "#9e9e9e";
    const safeColor = color || "#9e9e9e"; // Fallback color

    switch (status) {
      case "New":
        return `linear-gradient(135deg, ${safeColor}, #6a98ff)`;
      case "Contacted":
        return `linear-gradient(135deg, ${safeColor}, #a46aff)`;
      case "Qualified":
        return `linear-gradient(135deg, ${safeColor}, #00e396)`;
      case "Eligible":
        return `linear-gradient(135deg, ${safeColor}, #00e396)`;
      case "Not Eligible":
        return `linear-gradient(135deg, ${safeColor}, #ff4560)`;
      case "Pending":
        return `linear-gradient(135deg, ${safeColor}, #feb019)`;
      default:
        return `linear-gradient(135deg, ${safeColor}, ${alpha(
          safeColor,
          0.7
        )})`;
    }
  };

  const renderCellContent = (row, column) => {
    const { id, render } = column;

    if (customCellRenderer && customCellRenderer[id]) {
      return customCellRenderer[id](row);
    }

    if (render) {
      return render(row);
    }

    switch (id) {
      case "contact":
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar
              sx={{
                background: getAvatarGradient(row.status, theme),
                width: 45,
                height: 45,
                mr: 2,
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                border: "2px solid white",
                position: "relative",
              }}
            >
              {getInitials(row?.name === undefined ? "" : row?.name)}
              {row.status && (
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: getStatusColor(row.status),
                        border: "2px solid white",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                      }}
                    />
                  }
                />
              )}
            </Avatar>
            <Box>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{
                  fontSize: "0.95rem",
                  letterSpacing: "0.01em",
                  color: theme.palette.text.primary,
                }}
              >
                {row.name === undefined ? "-" : row.name}
              </Typography>
              {activeTab === 1 && row.income !== undefined && row.income && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.8rem",
                    mt: 0.5,
                    opacity: 0.8,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SellIcon
                    sx={{ fontSize: "0.9rem", mr: 0.5, opacity: 0.7 }}
                  />
                  Income: {row.income}
                </Typography>
              )}
            </Box>
          </Box>
        );
      case "status":
        const statusColor = getStatusColor
          ? getStatusColor(row.status)
          : theme.palette.grey[500];
        const safeStatusColor = statusColor || theme.palette.grey[500];
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              icon={getStatusIcon(row.status)}
              label={row.status === undefined ? "—" : row.status}
              sx={{
                bgcolor: alpha(safeStatusColor, 0.08),
                color: safeStatusColor,
                fontWeight: 600,
                minWidth: "110px",
                height: "32px",
                borderRadius: "16px",
                border: `1px solid ${alpha(safeStatusColor, 0.2)}`,
                boxShadow: `0 2px 4px ${alpha(safeStatusColor, 0.1)}`,
                "& .MuiChip-label": {
                  px: 1,
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiChip-icon": {
                  color: safeStatusColor,
                },
              }}
              size="medium"
            />
          </Box>
        );
      case "eligibilityScore":
        let scoreColor, scoreLabel, scoreBackground;
        const scoreValue = row.score || 0;

        if (scoreValue >= 70) {
          scoreColor = theme.palette.success?.main || "#4caf50";
          scoreLabel = "High";
          scoreBackground = `linear-gradient(135deg, ${alpha(
            scoreColor,
            0.15
          )}, ${alpha(theme.palette.success?.light || "#81c784", 0.15)})`;
        } else if (scoreValue >= 50) {
          scoreColor = theme.palette.warning?.main || "#ff9800";
          scoreLabel = "Medium";
          scoreBackground = `linear-gradient(135deg, ${alpha(
            scoreColor,
            0.15
          )}, ${alpha(theme.palette.warning?.light || "#ffb74d", 0.15)})`;
        } else {
          scoreColor = theme.palette.error?.main || "#f44336";
          scoreLabel = "Low";
          scoreBackground = `linear-gradient(135deg, ${alpha(
            scoreColor,
            0.15
          )}, ${alpha(theme.palette.error?.light || "#e57373", 0.15)})`;
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: scoreBackground,
                mr: 2,
                position: "relative",
                boxShadow: `0 5px 12px ${alpha(scoreColor, 0.25)}`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "50%",
                  border: `3px solid ${scoreColor}`,
                  opacity: 0.7,
                },
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  color: scoreColor,
                  textShadow: `0 1px 2px ${alpha(scoreColor, 0.3)}`,
                }}
              >
                {row.score === undefined ? "—" : scoreValue}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color={scoreColor}
                sx={{
                  fontSize: "0.9rem",
                  mb: 0.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TrendingUpIcon sx={{ mr: 0.5, fontSize: "0.9rem" }} />
                {row.score === undefined ? "—" : scoreLabel}
              </Typography>
              <Box sx={{ width: 130 }}>
                <LinearProgress
                  variant="determinate"
                  value={scoreValue}
                  sx={{
                    height: 5,
                    borderRadius: 5,
                    bgcolor: alpha(scoreColor, 0.15),
                    ".MuiLinearProgress-bar": {
                      bgcolor: scoreColor,
                      borderRadius: 5,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    fontSize: "0.7rem",
                    opacity: 0.8,
                  }}
                >
                  Eligibility Score
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      case "actions":
        return (
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <IconButton
              size="small"
              onClick={(e) => handleOpenActionMenu(e, row)}
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.08),
                width: 36,
                height: 36,
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                },
              }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      case "date":
        if (row[id] === undefined || !row[id]) return "-";
        try {
          const date = new Date(row[id]);
          const today = new Date();
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);

          // Format the date part
          let formattedDate;
          let dateColor = theme.palette.text.primary;

          if (date.toDateString() === today.toDateString()) {
            formattedDate = "Today";
            dateColor = theme.palette.success.main;
          } else if (date.toDateString() === yesterday.toDateString()) {
            formattedDate = "Yesterday";
            dateColor = theme.palette.info.main;
          } else {
            formattedDate = date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          }

          // Format the time part
          const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  color: dateColor,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CalendarIcon
                  sx={{
                    fontSize: "0.9rem",
                    mr: 0.75,
                    color: alpha(dateColor, 0.8),
                  }}
                />
                {formattedDate}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 0.3,
                  display: "flex",
                  alignItems: "center",
                  opacity: 0.8,
                }}
              >
                <TimeIcon sx={{ fontSize: "0.75rem", mr: 0.5 }} />
                {formattedTime}
              </Typography>
            </Box>
          );
        } catch (e) {
          return row[id] === "" ? "-" : row[id];
        }
      default:
        if (row[id] === undefined) {
          return (
            <TextValue
              variant="body2"
              sx={{ opacity: 0.5, fontStyle: "italic" }}
            >
              —
            </TextValue>
          );
        } else if (row[id] === null) {
          return (
            <TextValue
              variant="body2"
              sx={{ opacity: 0.5, fontStyle: "italic" }}
            >
              —
            </TextValue>
          );
        } else if (typeof row[id] === "boolean") {
          const boolColor = row[id] ? "success" : "default";
          return (
            <Box
              component={ValueChip}
              color={boolColor}
              size="small"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {row[id] ? (
                <CheckIcon
                  fontSize="small"
                  sx={{ mr: 0.75, fontSize: "1rem" }}
                />
              ) : null}
              {row[id] ? "Yes" : "No"}
            </Box>
          );
        } else if (Array.isArray(row[id])) {
          if (row[id].length === 0)
            return (
              <TextValue
                variant="body2"
                sx={{ opacity: 0.5, fontStyle: "italic" }}
              >
                —
              </TextValue>
            );
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {row[id].map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  size="small"
                  sx={{
                    height: "24px",
                    fontSize: "0.75rem",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    borderRadius: "12px",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                    boxShadow: `0 2px 5px ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                  }}
                />
              ))}
            </Box>
          );
        } else if (typeof row[id] === "object") {
          return (
            <TextValue variant="body2">
              {JSON.stringify(row[id]) || "—"}
            </TextValue>
          );
        } else if (row[id] === "") {
          return (
            <TextValue
              variant="body2"
              sx={{ opacity: 0.5, fontStyle: "italic" }}
            >
              —
            </TextValue>
          );
        }

        // Special handling for longitude and latitude
        if (
          id.toLowerCase().includes("longitude") ||
          id.toLowerCase().includes("latitude")
        ) {
          if (
            row[id] === undefined ||
            row[id] === null ||
            row[id] === "" ||
            String(row[id]).toLowerCase() === "undefined"
          ) {
            return (
              <TextValue
                variant="body2"
                sx={{ opacity: 0.5, fontStyle: "italic" }}
              >
                —
              </TextValue>
            );
          }

          return (
            <Box
              component={ValueChip}
              color="info"
              size="small"
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: "0.9rem",
                  mr: 0.75,
                  opacity: 0.9,
                },
              }}
            >
              <Box
                component="span"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: theme.palette.info.main,
                    mr: 1,
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.info.main,
                      0.2
                    )}`,
                  }}
                />
                {String(row[id])}
              </Box>
            </Box>
          );
        }

        // Try to handle strings that might contain numbers with decimals
        if (typeof row[id] === "string") {
          // Check if the string is a number (including decimals)
          const numericValue = parseFloat(row[id]);
          if (!isNaN(numericValue) && row[id].indexOf(".") !== -1) {
            // It's a numeric string with decimals
            const formattedNumber = numericValue.toFixed(2);

            // Handle percentage values
            if (
              id.toLowerCase().includes("percent") ||
              id.toLowerCase().includes("rate")
            ) {
              return (
                <Box
                  component={ValueChip}
                  color="info"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TrendingUpIcon sx={{ mr: 0.75, fontSize: "1rem" }} />
                  {formattedNumber}%
                </Box>
              );
            }

            // Handle monetary values
            if (
              id.toLowerCase().includes("price") ||
              id.toLowerCase().includes("cost") ||
              id.toLowerCase().includes("value") ||
              id.toLowerCase().includes("income") ||
              id.toLowerCase().includes("payment") ||
              id.toLowerCase().includes("loan") ||
              id.toLowerCase().includes("sales")
            ) {
              return (
                <Box
                  component={ValueChip}
                  color="success"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SellIcon sx={{ mr: 0.75, fontSize: "1rem" }} />₹
                  {parseFloat(formattedNumber).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Box>
              );
            }

            // Return formatted decimal for other values
            return (
              <Box component={ValueChip} color="default" size="small">
                {formattedNumber}
              </Box>
            );
          }

          // Format date strings if they look like dates but weren't caught by the date case
          if (row[id].match(/^\d{4}-\d{2}-\d{2}/)) {
            try {
              const date = new Date(row[id]);
              return (
                <Box
                  component={ValueChip}
                  color="default"
                  size="small"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CalendarIcon sx={{ mr: 0.75, fontSize: "1rem" }} />
                  {date.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </Box>
              );
            } catch (e) {
              // If parsing fails, return the original string
              return <TextValue variant="body2">{row[id]}</TextValue>;
            }
          }

          // For emails, special styling
          if (id.toLowerCase().includes("email") || row[id].includes("@")) {
            return (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component={ValueChip}
                  color="primary"
                  size="small"
                  sx={{
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <EmailIcon sx={{ mr: 0.75, fontSize: "1rem" }} />
                  {row[id]}
                </Box>
              </Box>
            );
          }

          // For phone numbers, special styling
          if (
            id.toLowerCase().includes("phone") ||
            id.toLowerCase().includes("mobile") ||
            /^(\+\d{1,3}[ -]?)?\(?\d{3}\)?[ -]?\d{3}[ -]?\d{4}$/.test(row[id])
          ) {
            return (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box component={ValueChip} color="info" size="small">
                  <PhoneIcon sx={{ mr: 0.75, fontSize: "1rem" }} />
                  {row[id]}
                </Box>
              </Box>
            );
          }

          // For regular strings - categorize some common field types
          if (
            id.toLowerCase().includes("type") ||
            id.toLowerCase().includes("category") ||
            id.toLowerCase().includes("status") ||
            id.toLowerCase().includes("role")
          ) {
            return (
              <Box
                component={ValueChip}
                color="default"
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.07),
                  color: theme.palette.primary.main,
                }}
              >
                {row[id]}
              </Box>
            );
          }

          // For regular strings
          return <TextValue variant="body2">{row[id]}</TextValue>;
        }

        // Format numeric values
        if (typeof row[id] === "number") {
          // For decimal values, show only 2 digits after decimal point
          if (row[id] % 1 !== 0) {
            // It's a decimal number
            const formattedNumber = row[id].toFixed(2);

            // Handle percentage values
            if (
              id.toLowerCase().includes("percent") ||
              id.toLowerCase().includes("rate")
            ) {
              return (
                <Box
                  component={ValueChip}
                  color="info"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TrendingUpIcon sx={{ mr: 0.75, fontSize: "1rem" }} />
                  {formattedNumber}%
                </Box>
              );
            }

            // Handle monetary values
            if (
              id.toLowerCase().includes("price") ||
              id.toLowerCase().includes("cost") ||
              id.toLowerCase().includes("value") ||
              id.toLowerCase().includes("income") ||
              id.toLowerCase().includes("payment") ||
              id.toLowerCase().includes("loan") ||
              id.toLowerCase().includes("sales")
            ) {
              return (
                <Box
                  component={ValueChip}
                  color="success"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SellIcon sx={{ mr: 0.75, fontSize: "1rem" }} />₹
                  {parseFloat(formattedNumber).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Box>
              );
            }

            // Return formatted decimal for other number types
            return (
              <Box component={ValueChip} color="default" size="small">
                {formattedNumber}
              </Box>
            );
          }

          // Handle percentage values for whole numbers
          if (
            id.toLowerCase().includes("percent") ||
            id.toLowerCase().includes("rate")
          ) {
            return (
              <Box
                component={ValueChip}
                color="info"
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TrendingUpIcon sx={{ mr: 0.75, fontSize: "1rem" }} />
                {row[id]}%
              </Box>
            );
          }

          // Handle monetary values for whole numbers
          if (
            id.toLowerCase().includes("price") ||
            id.toLowerCase().includes("cost") ||
            id.toLowerCase().includes("value") ||
            id.toLowerCase().includes("income") ||
            id.toLowerCase().includes("payment") ||
            id.toLowerCase().includes("loan") ||
            id.toLowerCase().includes("sales")
          ) {
            return (
              <Box
                component={ValueChip}
                color="success"
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <SellIcon sx={{ mr: 0.75, fontSize: "1rem" }} />₹
                {row[id].toLocaleString()}
              </Box>
            );
          }

          // For score or rating values
          if (
            id.toLowerCase().includes("score") ||
            id.toLowerCase().includes("rating")
          ) {
            let scoreColor;
            if (row[id] >= 70) {
              scoreColor = "success";
            } else if (row[id] >= 50) {
              scoreColor = "warning";
            } else {
              scoreColor = "error";
            }

            return (
              <Box
                component={ValueChip}
                color={scoreColor}
                size="small"
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {row[id]}
              </Box>
            );
          }

          // For age/year values
          if (
            id.toLowerCase().includes("age") ||
            id.toLowerCase().includes("year")
          ) {
            return (
              <Box
                component={ValueChip}
                color="primary"
                size="small"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: alpha(theme.palette.primary.main, 0.07),
                }}
              >
                {row[id]} {id.toLowerCase().includes("age") ? "years" : ""}
              </Box>
            );
          }

          // For other whole numbers
          return (
            <Box component={ValueChip} color="default" size="small">
              {row[id].toLocaleString()}
            </Box>
          );
        }

        // Default case for any other type
        return <TextValue variant="body2">{String(row[id])}</TextValue>;
    }
  };

  // const pagedData = useMemo(
  //   () => filteredTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
  //   [filteredTableData, page, rowsPerPage]
  // );

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: "28px",
          overflow: "hidden",
          border: `1px solid ${alpha(
            theme.palette?.divider || "#e0e0e0",
            0.4
          )}`,
          boxShadow:
            theme.palette?.mode === "light"
              ? "0 15px 40px rgba(0,0,0,0.07), 0 10px 20px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.03)"
              : "0 15px 40px rgba(0,0,0,0.2), 0 5px 10px rgba(0,0,0,0.1)",
          background:
            theme.palette?.mode === "light"
              ? "#ffffff"
              : alpha(theme.palette?.background?.paper || "#121212", 0.8),
          position: "relative",
          backdropFilter: "blur(10px)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: `linear-gradient(90deg, 
                            ${theme.palette?.primary?.dark || "#1565c0"} 0%, 
                            ${theme.palette?.primary?.main || "#2196f3"} 50%,
                            ${theme.palette?.primary?.dark || "#1565c0"} 100%)`,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 5,
            left: 0,
            right: 0,
            height: "1px",
            background:
              theme.palette?.mode === "light"
                ? "rgba(255, 255, 255, 0.65)"
                : alpha(theme.palette?.common?.white || "#ffffff", 0.07),
          },
        }}
      >
        {hasActiveFilters && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}` }}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={clearAllFilters}
              startIcon={<CancelIcon />}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.75rem"
              }}
            >
              Reset All Filters
            </Button>
          </Box>
        )}
        <Box sx={{ overflow: "hidden", borderRadius: "24px" }}>
          <TableContainer sx={{ overflowX: "auto", overflowY: "hidden" }}>
            <Table sx={{ minWidth: 750, tableLayout: "auto", overflow: "hidden" }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <StyledTableCell
                      key={column.id}
                      align={column.align || "left"}
                      sx={{
                        ...column.headerStyle,
                        verticalAlign: "bottom",
                        maxWidth: column.maxWidth || "none",
                        width: column.maxWidth || "auto",
                        minWidth: computedColumnWidths[column.id] || column.minWidth || undefined,
                      }}>
                      <Box 
                        sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "space-between", 
                          gap: 1.5,
                          cursor: column.id !== "actions" ? "pointer" : "default",
                          padding: column.id !== "actions" ? "8px 12px" : "0",
                          borderRadius: column.id !== "actions" ? "12px" : "0",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          position: "relative",
                          "&:hover": column.id !== "actions" ? {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            transform: "translateY(-1px)",
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            "&::before": {
                              opacity: 1,
                              transform: "scale(1)",
                            }
                          } : {},
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: "12px",
                            background: `linear-gradient(135deg, 
                              ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                              ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                            opacity: 0,
                            transform: "scale(0.95)",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            zIndex: -1,
                          }
                        }}
                        onClick={column.id !== "actions" ? () => handleSort(column.id) : undefined}
                      >
                        <Typography 
                          component="div" 
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            letterSpacing: "0.03em",
                            color: theme.palette.mode === "light" 
                              ? alpha(theme.palette.primary.dark, 0.9)
                              : theme.palette.primary.light,
                            textShadow: theme.palette.mode === "light" 
                              ? "0 1px 2px rgba(0,0,0,0.05)"
                              : "0 1px 2px rgba(255,255,255,0.1)",
                            transform: column.id === "actions" ? "translateY(-8px)" : "none",
                            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          {column.label}
                        </Typography>
                        {column.id !== "actions" && getSortIcon(column.id) && (
                          <Box 
                            sx={{ 
                              display: "flex", 
                              alignItems: "center",
                              padding: "4px",
                              borderRadius: "8px",
                              backgroundColor: alpha(theme.palette.primary.main, 0.06),
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                transform: "scale(1.1)",
                              }
                            }}
                          >
                            {getSortIcon(column.id)}
                          </Box>
                        )}
                                                   {/* {column.id !== "actions" && (
                            <IconButton size="small" onClick={(e) => openFilterMenu(e, column.id)}>
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                          )} */}
                      </Box>
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={getColSpan()}
                      align="center"
                      sx={{ py: 16 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          animation: "fadeIn 0.6s ease",
                        }}
                      >
                        <CircularProgress
                          size={80}
                          thickness={2.5}
                          sx={{
                            color: alpha(
                              theme.palette?.primary?.main || "#2196f3",
                              0.7
                            ),
                            position: "absolute",
                            opacity: 0.85,
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredTableData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={getColSpan()}
                      align="center"
                      sx={{ py: 14 }}
                    >
                      <Box
                        sx={{
                          py: 6,
                          px: 8,
                          borderRadius: "30px",
                          background: `linear-gradient(135deg,
                          ${theme.palette?.primary?.main || "#2196f3"} 0%,
                          ${theme.palette?.primary?.dark || "#1565c0"} 100%)`,
                          display: "inline-block",
                          backdropFilter: "blur(20px)",
                          border: `1px dashed ${alpha(
                            theme.palette?.primary?.main || "#2196f3",
                            0.25
                          )}`,
                          boxShadow: `0 20px 40px ${alpha(
                            theme.palette?.primary?.main || "#2196f3",
                            0.04
                          )}`,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            mb: 2,
                            color: theme.palette?.primary?.main || "#2196f3",
                            textAlign: "center",
                            fontFamily: "'Poppins', sans-serif",
                            letterSpacing: "0.02em",
                            textShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                        >
                          No Data Found
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            mb: 5,
                            maxWidth: 500,
                            textAlign: "center",
                            lineHeight: 1.8,
                            fontFamily: "'Poppins', sans-serif",
                            px: 2,
                          }}
                        >
                          {noDataMessage ||
                            "No records match your filters."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTableData.map((row) => (
                    <StyledTableRow key={row.id}>
                      {columns.map((column) => (
                        <StyledTableCell
                          key={`${row.id}-${column.id}`}
                          align={column.align || "left"}
                          sx={{
                            maxWidth: column.maxWidth || "none",
                            width: column.maxWidth || "auto",
                            minWidth: computedColumnWidths[column.id] || column.minWidth || undefined,
                            ...(column.id === "contact" && { minWidth: Math.max(220, computedColumnWidths[column.id] || 0) }),
                            ...(column.id === "date" && { minWidth: Math.max(140, computedColumnWidths[column.id] || 0) }),
                            ...(column.id === "status" && { minWidth: Math.max(120, computedColumnWidths[column.id] || 0) }),
                            ...(column.id === "eligibilityScore" && {
                              minWidth: Math.max(200, computedColumnWidths[column.id] || 0),
                            }),
                          }}
                        >
                          {renderCellContent(row, column)}
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeFilterMenu}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          transformOrigin={{ horizontal: "left", vertical: "top" }}
          sx={{ "& .MuiPaper-root": { maxHeight: 360, width: 280 } }}
        >
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {menuColumnId}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
            {uniqueValuesForColumn(menuColumnId || "").map((val) => {
              const checked = (multiFilters[menuColumnId || ""] || new Set()).has(val);
              return (
                <MenuItem key={val} onClick={() => toggleValue(menuColumnId || "", val)}>
                  <Checkbox size="small" checked={checked} sx={{ mr: 1 }} />
                  <ListItemText primary={val} />
                </MenuItem>
              );
            })}
            {uniqueValuesForColumn(menuColumnId || "").length === 0 && (
              <Typography variant="body2" sx={{ px: 2, py: 1.5, color: "text.secondary" }}>
                No values
              </Typography>
            )}
          </Box>
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.5, py: 1 }}>
            <Button size="small" color="secondary" onClick={() => { clearColumnFilter(menuColumnId || ""); }}>
              Clear
            </Button>
            <Button size="small" variant="contained" onClick={closeFilterMenu}>
              Apply
            </Button>
          </Box>
        </Menu>

        <Menu
          id="action-menu"
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleCloseActionMenu}
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "24px",
              boxShadow:
                "0 15px 40px rgba(0,0,0,0.18), 0 5px 15px rgba(0,0,0,0.05)",
              minWidth: 230,
              overflow: "visible",
              mt: 2,
              border: `1px solid ${alpha(
                theme.palette?.divider || "#e0e0e0",
                0.07
              )}`,
              backgroundImage:
                theme.palette?.mode === "light"
                  ? "linear-gradient(145deg, rgba(252, 253, 255, 0.95), rgba(246, 249, 255, 0.95))"
                  : "linear-gradient(145deg, rgba(26, 32, 47, 0.95), rgba(18, 22, 34, 0.95))",
              backdropFilter: "blur(20px)",
              "&:before": {
                content: '""',
                position: "absolute",
                top: -10,
                right: 22,
                width: 20,
                height: 20,
                transform: "rotate(45deg)",
                borderTop: `1px solid ${alpha(
                  theme.palette?.divider || "#e0e0e0",
                  0.07
                )}`,
                borderLeft: `1px solid ${alpha(
                  theme.palette?.divider || "#e0e0e0",
                  0.07
                )}`,
                backgroundColor:
                  theme.palette?.mode === "light"
                    ? "rgba(252, 253, 255, 0.95)"
                    : "rgba(26, 32, 47, 0.95)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {/* View option - only shown if onViewClick is provided */}
          {permissions.view === 1 &&
            onViewClick && (
              <MenuItem
                onClick={() => {
                  handleCloseActionMenu();
                  onViewClick && onViewClick(selectedRow);
                }}
                sx={{
                  py: 1.75,
                  borderRadius: "12px",
                  mx: 1,
                  mb: 0.5,
                  color: theme.palette.info.main,
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                      color: theme.palette.info.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="View"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "0.95rem",
                    letterSpacing: "0.01em",
                  }}
                />
              </MenuItem>
            )}

          {permissions?.edit === 1 && onEditClick && (
            <MenuItem
              onClick={() => {
                handleCloseActionMenu();
                onEditClick && onEditClick(selectedRow);
              }}
              sx={{
                py: 1.75,
                borderRadius: "12px",
                mx: 1,
                mb: 0,
              }}
            >
              <ListItemIcon>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    width: 36,
                    height: 36,
                  }}
                >
                  <EditIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary="Edit"
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "0.95rem",
                  letterSpacing: "0.01em",
                }}
              />
            </MenuItem>
          )}

          {permissions?.edit === 1 && handleUpdateStatus && (
            <>
              <MenuItem
                onClick={() => {
                  handleCloseActionMenu();
                  handleUpdateStatus(selectedRow?.id, "LOGGED_IN");
                }}
                sx={{
                  py: 1.75,
                  borderRadius: "12px",
                  mx: 1,
                  mb: 0,
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="Logged In"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "0.95rem",
                    letterSpacing: "0.01em",
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleCloseActionMenu();
                  handleUpdateStatus(selectedRow?.id, "UNDER_FOLLOW_UP");
                }}
                sx={{
                  py: 1.75,
                  borderRadius: "12px",
                  mx: 1,
                  mb: 0,
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary="Under Follow Up"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "0.95rem",
                    letterSpacing: "0.01em",
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleCloseActionMenu();
                  handleUpdateStatus(selectedRow?.id, "NOT_ELIGIBLE");
                }}
                sx={{
                  py: 1.75,
                  borderRadius: "12px",
                  mx: 1,
                  mb: 0,
                }}
              >
                <ListItemIcon>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary="Not Eligible" primaryTypographyProps={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", letterSpacing: "0.01em" }} />
              </MenuItem>
            </>
          )}

          {/* Apply Loan menu item - only shown for borrowers */}
          {isBorrower && selectedRow && isBorrower(selectedRow) && (
            <MenuItem
              onClick={() => {
                handleCloseActionMenu();
                onApplyLoanClick && onApplyLoanClick(selectedRow);
              }}
              sx={{
                py: 1.75,
                borderRadius: "12px",
                mx: 1,
                mb: 0,
                color: theme.palette.success.main,
              }}
            >
              <ListItemIcon>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.main, width: 36, height: 36 }}>
                  <MonetizationOnIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText primary="Apply Loan" primaryTypographyProps={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", letterSpacing: "0.01em", color: "inherit" }} />
            </MenuItem>
          )}

          <Divider sx={{ my: 1.75, mx: 3, opacity: 0.5 }} />
          {permissions?.delete === 1 && (
            <MenuItem
              onClick={() => {
                handleCloseActionMenu();
                onDeleteClick && onDeleteClick(selectedRow);
              }}
              sx={{
                py: 1.75,
                color: theme.palette.error.main,
                borderRadius: "12px",
                mx: 1,
                mt: 0,
              }}
            >
              <ListItemIcon>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.main, width: 36, height: 36 }}>
                  <DeleteIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText primary="Delete" primaryTypographyProps={{ fontWeight: 600, color: "inherit", fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", letterSpacing: "0.01em" }} />
            </MenuItem>
          )}
        </Menu>

        {/* Pagination section */}
        {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 3.5, px: 5, position: "relative", overflow: "hidden", backgroundColor: theme.palette.mode === "light" ? `linear-gradient(to right, rgba(252, 253, 255, 0.97), rgba(246, 249, 255, 0.97))` : alpha(theme.palette.grey[900], 0.45), borderTop: `1px solid ${alpha(theme.palette.divider, 0.06)}` }}> */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              py: 0,
              fontFamily: "'Poppins', sans-serif",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                fontSize: "0.875rem",
                fontWeight: 500,
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: "0.01em",
              },
              ".MuiTablePagination-select": {
                paddingY: 0.5,
                paddingX: 0.5,
                borderRadius: "8px",
                border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                marginRight: 2,
                fontWeight: 500,
              },
              ".MuiButtonBase-root": {
                borderRadius: "12px",
                mx: 0.5,
                width: 36,
                height: 36,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  transform: "translateY(-1px)",
                },
              },
              ".Mui-selected": {
                backgroundColor: `${alpha(
                  theme.palette.primary.main,
                  0.12
                )} !important`,
                fontWeight: 600,
                color: theme.palette.primary.main,
              },
            }}
          />
        {/* </Box> */}
      </Card>
    </>
  );
};

export default CommonTable;
